import { ModuleRequestError } from '@wit-support/admin-client-core';
/*
 * Экономия запросов раздела (план meta-economy, U1). Почти каждый запрос
 * раздела уходит через admin-api в auto-ads, а часть — дальше в Meta, где
 * лимит частоты общий для всех сервисов. Поэтому здесь правила, которые
 * действуют ТОЛЬКО на запросы «Запусков FB» (глобальные умолчания
 * QueryClient admin-web другим модулям не меняем):
 * - не перечитывать при возврате во вкладку и при восстановлении сети;
 * - повторять только сбой сети/недоступность сервиса и не больше одного раза;
 *   отказ (4xx), ограничение частоты (429 / лимит Meta) и ответ Meta — никогда;
 * - опрашивать только то, что ещё меняется само.
 */
/** Справочники (кабинеты, списки стран, шаблоны, файлы кабинета) — 5 мин. */
export const REFERENCE_STALE_MS = 5 * 60_000;
/** Поиск интересов Meta — 10 мин: ответ на тот же запрос не меняется. */
export const SEARCH_STALE_MS = 10 * 60_000;
/** «Уже использовали» (интересы наших групп, без Meta) — 10 мин: auto-ads держит агрегат час. */
export const USED_INTERESTS_STALE_MS = 10 * 60_000;
/**
 * Коды ограничения частоты Graph API: 4 — приложение, 17 — пользователь,
 * 32 — страница, 613 — свой лимит вызова, 80000–80014 — лимиты бизнес-сценариев
 * (Ads Management, Ads Insights, …).
 */
const META_RATE_LIMIT_CODES = new Set([
    4,
    17,
    32,
    613,
    ...Array.from({ length: 15 }, (_, i) => 80_000 + i),
]);
/** Подкоды «слишком много вызовов рекламного кабинета». */
const META_RATE_LIMIT_SUBCODES = new Set([2446079, 1487742]);
/** Ошибка — ограничение частоты Meta, пришедшее как 502 `meta-error`. */
export function isMetaRateLimit(err) {
    if (!(err instanceof ModuleRequestError))
        return false;
    const meta = err.details['metaError'];
    if (meta === null || typeof meta !== 'object')
        return false;
    const { code, subcode } = meta;
    const c = typeof code === 'number' ? code : typeof code === 'string' ? Number(code) : NaN;
    const s = typeof subcode === 'number' ? subcode : NaN;
    return META_RATE_LIMIT_CODES.has(c) || META_RATE_LIMIT_SUBCODES.has(s);
}
/** Любое ограничение частоты: 429 / `rate-limited` auto-ads или лимит Meta. */
export function isRateLimited(err) {
    if (!(err instanceof ModuleRequestError))
        return false;
    return err.status === 429 || err.upstreamCode === 'rate-limited' || isMetaRateLimit(err);
}
/** Через сколько повторить по ответу (`Retry-After` / `retryAfterMs`), мс; null — не сказано. */
export function retryAfterMs(err) {
    if (!(err instanceof ModuleRequestError))
        return null;
    const sec = Number(err.details['retryAfterSec']);
    if (Number.isFinite(sec) && sec > 0)
        return Math.ceil(sec) * 1000;
    const ms = Number(err.details['retryAfterMs']);
    return Number.isFinite(ms) && ms > 0 ? ms : null;
}
/**
 * Стоит ли повторить запрос раздела (`retry` TanStack Query; `failureCount` —
 * сколько повторов уже было, первый сбой — 0).
 *
 * Повтор — максимум один и только для временного сбоя: сеть (`TypeError`
 * fetch) или 5xx «сервис недоступен» (`upstream_unavailable`, голый `http_5xx`).
 * Не повторяем: 4xx (в т.ч. 429), ограничение частоты в любом виде,
 * `upstream_rejected` с 5xx (`meta-error` — это ответ Meta, а не поломка),
 * «не настроен» / «конструктор выключен» (503 — повтор их не исправит),
 * ответ не той формы (ошибка разбора).
 */
export function shouldRetryAdsQuery(failureCount, error) {
    if (failureCount >= 1)
        return false;
    if (error instanceof ModuleRequestError) {
        if (isRateLimited(error))
            return false;
        if (error.status < 500)
            return false;
        if (error.code === 'upstream_rejected')
            return false;
        if (error.code === 'ads_not_configured' || error.code === 'ads_builder_disabled')
            return false;
        return true;
    }
    // fetch бросает TypeError, когда ответа нет вовсе (сеть, CORS, обрыв)
    return error instanceof TypeError;
}
/**
 * Общие опции запросов раздела — подмешиваются в каждый `useQuery` /
 * `useInfiniteQuery` модуля.
 */
export const ADS_QUERY_DEFAULTS = {
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // опрос (`refetchInterval`) — только при видимой вкладке (так и по умолчанию; явно)
    refetchIntervalInBackground: false,
    retry: shouldRetryAdsQuery,
};
// --- опрос ------------------------------------------------------------------
//
// GET /launches/:id и GET /batches* auto-ads отдаёт из своей базы (вызовов Meta
// нет), поэтому опрос нужен, пока что-то меняется само, — но реже, если
// изменения ждут времени (старт, сверка после включения), а не идут прямо сейчас.
// Опрос идёт только при видимой вкладке (`refetchIntervalInBackground: false`).
/**
 * Статусы запуска, которые меняются прямо сейчас (оркестратор auto-ads в
 * работе). `scheduled` («создастся в 00:00 / в указанное время») — НЕ здесь:
 * это ожидание времени, как `waiting_start`.
 */
export const LAUNCH_ACTIVE_STATUSES = new Set([
    'creating',
    'activating',
    'rolling_back',
]);
/**
 * Конечные статусы запуска: сами больше не меняются, только действием человека
 * (оно сбрасывает кэш). Всё прочее — в том числе новый статус auto-ads — ждёт
 * времени или сверки: старт (`waiting_start`), сверка после включения
 * (`running`, `needs_attention`), повтор паузы (`pause_failed`), автовключение
 * (`ready_paused`).
 */
export const LAUNCH_TERMINAL_STATUSES = new Set([
    'draft',
    'blocked',
    'cancelled',
    'paused',
    'paused_on_drift',
    'failed',
    'rolled_back',
]);
/** Статусы запуска, ждущие времени (создание по плану, старт). */
const LAUNCH_SCHEDULED_STATUSES = ['waiting_start', 'scheduled'];
export const LAUNCH_POLL_MS = 3000;
export const BATCH_POLL_MS = 4000;
/** Ждущие не-конечные запуск / пачка. */
export const WAITING_POLL_MS = 30_000;
export const BATCHES_LIST_POLL_MS = 15_000;
export const BATCHES_LIST_WAITING_POLL_MS = 60_000;
/** Первая загрузка карточки упала — повторная попытка (восстановление). */
export const ERROR_RECOVERY_POLL_MS = 30_000;
export function launchPhase(displayStatus) {
    if (LAUNCH_ACTIVE_STATUSES.has(displayStatus))
        return 'active';
    return LAUNCH_TERMINAL_STATUSES.has(displayStatus) ? 'terminal' : 'waiting';
}
/**
 * Опрос карточки, у которой ещё нет данных: первая загрузка упала —
 * пробуем снова раз в 30 с (сеть, «недоступен», лимит — не раньше
 * `Retry-After`); 4xx, кроме 429, не пройдёт сам (нет такого, нет прав) —
 * не опрашиваем. Нет ни данных, ни ошибки (идёт загрузка) — `false`.
 */
export function recoveryPollMs(error) {
    if (error === null || error === undefined)
        return false;
    if (isRateLimited(error))
        return Math.max(ERROR_RECOVERY_POLL_MS, retryAfterMs(error) ?? 0);
    if (error instanceof ModuleRequestError && error.status >= 400 && error.status < 500) {
        return false;
    }
    return ERROR_RECOVERY_POLL_MS;
}
/** `refetchInterval` карточки: по данным, а без данных — восстановление после сбоя. */
export function detailPollMs(state, byData) {
    return state.data === undefined ? recoveryPollMs(state.error) : byData(state.data);
}
/** Карточка запуска: в работе — 3 с, ждёт — 30 с, конечный — без опроса. */
export function launchPollMs(launch) {
    if (!launch)
        return false;
    const phase = launchPhase(launch.displayStatus);
    return phase === 'active' ? LAUNCH_POLL_MS : phase === 'waiting' ? WAITING_POLL_MS : false;
}
const countsOf = (b) => b.counts;
const hasAny = (b, statuses) => {
    const c = countsOf(b);
    for (const s of statuses)
        if ((c[s] ?? 0) > 0)
            return true;
    return false;
};
/** Конечные состояния пачки (если в ней не осталось ждущих запусков). */
const BATCH_TERMINAL_STATES = new Set([
    'failed',
    'done_manual',
    'cancelled',
    'rolled_back',
]);
/**
 * Пачка меняется прямо сейчас: в ней есть запуск в работе, она только что
 * создана для немедленного исполнения (`draft` не в режиме черновика) или
 * исполняется (`running`) — если только её запуски не ждут времени
 * (`scheduled` / `waiting_start`: тогда это ожидание, 30 с). Черновик
 * (`mode: 'draft'`) ждёт человека — сам не меняется.
 */
export function isBatchLive(b) {
    if (hasAny(b, LAUNCH_ACTIVE_STATUSES))
        return true;
    if (b.state === 'draft' && b.mode !== 'draft')
        return true;
    return b.state === 'running' && !hasAny(b, LAUNCH_SCHEDULED_STATUSES);
}
/**
 * Фаза пачки: `active` — см. `isBatchLive`; `waiting` — есть ждущий
 * не-конечный запуск или пачка в не-конечном состоянии (`active`,
 * `needs_attention`, новое состояние auto-ads); иначе — `terminal`.
 */
export function batchPhase(b) {
    if (isBatchLive(b))
        return 'active';
    const c = countsOf(b);
    for (const [s, n] of Object.entries(c)) {
        if (s === 'total' || (n ?? 0) === 0)
            continue;
        if (launchPhase(s) === 'waiting')
            return 'waiting';
    }
    if (b.state === 'draft' || BATCH_TERMINAL_STATES.has(b.state))
        return 'terminal';
    return 'waiting';
}
/** Карточка пачки: в работе — 4 с, ждёт — 30 с, конечная — без опроса. */
export function batchPollMs(batch) {
    if (!batch)
        return false;
    const phase = batchPhase(batch);
    return phase === 'active' ? BATCH_POLL_MS : phase === 'waiting' ? WAITING_POLL_MS : false;
}
/** Насколько вперёд список пачек ждёт ручного старта (ревью E-2 P3): дальше — без опроса. */
export const MANUAL_START_POLL_HORIZON_MS = 24 * 60 * 60_000;
/**
 * Список пачек (открытая страница): есть пачка в работе (`isBatchLive`) —
 * 15 с; есть запуск, ждущий времени (`scheduled` / `waiting_start`), — 60 с;
 * есть запуск, ждущий ручного включения, со стартом не позже чем через 24 ч
 * (`manualStartNextAt` сервера; E-2 P2-5: «1 запуск ждёт включения» появится
 * сам), — 60 с; давно забытые (опоздавшие уже в `lateStart`) и далёкие — без
 * опроса. Иначе — без опроса.
 */
export function batchesListPollMs(items, now = Date.now()) {
    if (!items || items.length === 0)
        return false;
    if (items.some(isBatchLive))
        return BATCHES_LIST_POLL_MS;
    if (items.some((b) => hasAny(b, LAUNCH_SCHEDULED_STATUSES)))
        return BATCHES_LIST_WAITING_POLL_MS;
    const soon = items.some((b) => {
        const at = b.manualStartNextAt != null ? Date.parse(b.manualStartNextAt) : NaN;
        return Number.isFinite(at) && at <= now + MANUAL_START_POLL_HORIZON_MS;
    });
    return soon ? BATCHES_LIST_WAITING_POLL_MS : false;
}
// --- медиатека: опрос только занятых файлов ---------------------------------
/** Копия файла ещё переносится в кабинет — меняется сама. */
export const MEDIA_BUSY_STATES = new Set([
    'queued',
    'uploading',
    'processing',
]);
export const MEDIA_BUSY_POLL_MS = 5000;
/** Сколько занятых файлов опрашиваем за раз (остальные — когда эти закончат). */
export const MEDIA_BUSY_POLL_MAX = 10;
export function isAssetBusy(a) {
    return a.byAccount.some((m) => MEDIA_BUSY_STATES.has(m.state));
}
/** id занятых файлов загруженных страниц — в порядке показа, не больше `max`. */
export function busyAssetIds(data, max = MEDIA_BUSY_POLL_MAX) {
    const out = [];
    for (const p of data?.pages ?? []) {
        for (const a of p.items) {
            if (out.length >= max)
                return out;
            if (isAssetBusy(a))
                out.push(a.assetId);
        }
    }
    return out;
}
/**
 * Подставить свежие файлы в уже загруженные страницы (по `assetId`) и убрать
 * исчезнувшие (`gone` — 404/410 карточки файла). Остальное — как было.
 */
export function withAssetsPatched(data, fresh, gone = []) {
    if (!data || (fresh.length === 0 && gone.length === 0))
        return data;
    const byId = new Map(fresh.map((a) => [a.assetId, a]));
    const drop = new Set(gone);
    return {
        ...data,
        pages: data.pages.map((p) => ({
            ...p,
            items: p.items.filter((a) => !drop.has(a.assetId)).map((a) => byId.get(a.assetId) ?? a),
        })),
    };
}
//# sourceMappingURL=query-policy.js.map